/**
 * REST-Client der Seite „E-Rechnung aus PDF" (Plan API 2026-11-01/01, Schritt 4).
 *
 * Zwei Wege, beide ueber die REST-Tuer mit Sitzungsschluessel wie die Konvertierung (KW-003):
 *
 * - `extract` — `POST /api/v1/invoice/de/pdf/extract`, Rumpf `{ pdf: base64 }`, Antwort nach E11
 *   (`{ invoice, fields, missing, checks, pageCount }`). 🔴 Die Route entsteht parallel im Backend
 *   (Schritt 3); die Antwortform ist hier aus dem Plan uebernommen und wird am Draht mit zod geprueft — eine
 *   abweichende Antwort wird `failed`, nie still durchgereicht.
 * - `create` — die BESTEHENDEN Routen `/de/zugferd/embed`, `/de/zugferd/generate`, `/de/xrechnung/generate`, je mit
 *   `validate: true` (E4, E6: die Gueltigkeitsanzeige kommt aus genau diesem Aufruf). Geschickt wird das im
 *   Pruefschritt bearbeitete `invoice` — begruendete Ausnahme von „Was der Server weiss, schickt ihm der Browser
 *   nicht zurueck" (Plan, Abschnitt „Schritt 3 an /embed"): das `invoice` ist hier Nutzereingabe, der Server haelt
 *   zwischen den Schritten keinen Zustand.
 *
 * 🔴 **401/403 → einmal ein neuer Sitzungsschluessel und eine Wiederholung** (E10): der Schluessel lebt 1 h und traegt
 * die Rechte vom Zeitpunkt seiner Ausgabe — nach einem Upgrade oder Release fehlt ihm das neue Recht. Genau ein
 * Versuch; eine zweite Absage ist die Antwort.
 *
 * Bewusst ein reiner Baustein ohne React (kein `useMemo` ueber Mutationen, `apiClientMemoDeps.test.ts`): die Seite
 * baut ihn einmal und reicht den Schluessel ueber `getKey` herein.
 */
import { z } from 'zod'

/** Grenze des PDF vor dem Hochladen (E11: hoechstens 10 MiB, die Route weist darueber mit 413 ab). */
export const PDF_MAX_BYTES = 10 * 1024 * 1024

// ─── Antwortform von /extract (E11) ──────────────────────────────────────────────────────────────

const fieldSourceSchema = z.enum(['pdf', 'profile']).nullable()

const partySchema = z.looseObject({
  name: z.string().nullish(),
  street: z.string().nullish(),
  postalCode: z.string().nullish(),
  city: z.string().nullish(),
  countryCode: z.string().nullish(),
  email: z.string().nullish(),
  vatId: z.string().nullish(),
  taxId: z.string().nullish(),
})

const itemSchema = z.looseObject({
  description: z.string().nullish(),
  quantity: z.number().nullish(),
  unit: z.string().nullish(),
  unitPrice: z.number().nullish(),
  netAmount: z.number().nullish(),
  taxRate: z.number().nullish(),
  taxAmount: z.number().nullish(),
  grossAmount: z.number().nullish(),
  /** E24: Zeitraum dieser Position (BT-134/135). */
  servicePeriod: z
    .looseObject({ start: z.string().nullish(), end: z.string().nullish() })
    .nullish(),
})

const invoiceDraftSchema = z.looseObject({
  type: z.string().nullish(),
  invoiceNumber: z.string().nullish(),
  issueDate: z.string().nullish(),
  dueDate: z.string().nullish(),
  currency: z.string().nullish(),
  seller: partySchema.nullish(),
  buyer: partySchema.nullish(),
  countrySpecific: z.looseObject({ buyerReference: z.string().nullish() }).nullish(),
  items: z.array(itemSchema).nullish(),
  subtotal: z.number().nullish(),
  total: z.number().nullish(),
})

const checkSchema = z.looseObject({
  code: z.string(),
  severity: z.enum(['error', 'warning']),
  message: z.string(),
  field: z.string().optional(),
})

const extractResponseSchema = z.looseObject({
  invoice: invoiceDraftSchema,
  fields: z.record(
    z.string(),
    z.looseObject({ value: z.unknown().optional(), source: fieldSourceSchema })
  ),
  missing: z.array(z.string()),
  checks: z.array(checkSchema),
  pageCount: z.number(),
  /** E19: `false` = weder Nummer noch Datum noch Betrag gefunden, das Auslesen zählte nicht. */
  recognized: z.boolean().optional(),
})

export type FieldSource = z.infer<typeof fieldSourceSchema>
export type InvoiceDraft = z.infer<typeof invoiceDraftSchema>
export type InvoiceDraftItem = z.infer<typeof itemSchema>
export type ExtractCheck = z.infer<typeof checkSchema>
export type ExtractResponse = z.infer<typeof extractResponseSchema>

// ─── Antwortform von /embed und /generate (bestehend, invoice-openapi.ts) ───────────────────────

const findingSchema = z.looseObject({
  code: z.string().optional(),
  message: z.string(),
  field: z.string().optional(),
})

const createdSchema = z.looseObject({
  success: z.literal(true),
  data: z.string(),
  mimeType: z.string(),
  filename: z.string(),
  embeddedXml: z.string().optional(),
  validation: z
    .looseObject({
      valid: z.boolean().nullable(),
      status: z.enum(['passed', 'failed', 'not_run']),
      errors: z.array(findingSchema).optional(),
    })
    .optional(),
  carrier: z
    .looseObject({
      pdfA3Declared: z.boolean(),
      warnings: z.array(findingSchema),
    })
    .optional(),
  warnings: z.array(findingSchema).optional(),
})

export type CreatedDocument = z.infer<typeof createdSchema>
export type Finding = z.infer<typeof findingSchema>

// ─── Ergebnisse ──────────────────────────────────────────────────────────────────────────────────

export interface QuotaRejection {
  current: number
  limit: number
}

/** Warum ein Aufruf nichts lieferte — jede Art hat auf der Seite ihren eigenen Satz. */
export type RequestFailure =
  | { kind: 'noKey' }
  | { kind: 'forbidden' }
  | { kind: 'quota'; quota: QuotaRejection }
  | { kind: 'failed'; message: string | null }

/** Ablehnungen von /extract nach der Entscheidungstabelle (X2, X6, X7, X8, X10). */
export type ExtractFailure =
  | RequestFailure
  | { kind: 'noDocument' }
  | { kind: 'noText' }
  | { kind: 'invalid' }
  | { kind: 'tooLarge' }
  | { kind: 'sellerIncomplete'; missing: string[] }

export type CreateFailure = RequestFailure | { kind: 'compliance'; errors: Finding[] }

export type ExtractOutcome =
  | { ok: true; result: ExtractResponse }
  | { ok: false; failure: ExtractFailure }

export type CreateOutcome =
  | { ok: true; result: CreatedDocument }
  | { ok: false; failure: CreateFailure }

/** Die drei Ausgaben von Schritt 3 (E4). */
export type OutputKind = 'embed' | 'generate' | 'xrechnung'

export const OUTPUT_KINDS: readonly OutputKind[] = ['embed', 'generate', 'xrechnung']

const OUTPUT_PATH: Record<OutputKind, string> = {
  embed: '/api/v1/invoice/de/zugferd/embed',
  generate: '/api/v1/invoice/de/zugferd/generate',
  xrechnung: '/api/v1/invoice/de/xrechnung/generate',
}

export interface CreateParams {
  output: OutputKind
  /** Base64 des Original-PDF — nur `embed` schickt es mit. */
  pdf: string
  invoice: InvoiceDraft
  /** Gespeicherte PDF-Vorlage fuer `generate`; ohne sie nimmt der Server seine Standardvorlage. */
  templateId?: string
}

export interface PdfExtractClient {
  extract: (pdf: string) => Promise<ExtractOutcome>
  create: (params: CreateParams) => Promise<CreateOutcome>
}

export interface PdfExtractClientOptions {
  baseUrl: string
  /** Der aktuelle Sitzungsschluessel (oder `null`, solange keiner da ist). */
  getKey: () => string | null
  /** Verwirft den Schluessel und holt einen neuen — `null`, wenn keiner ausgegeben wurde. */
  refreshKey: () => Promise<string | null>
  fetchImpl?: typeof fetch
}

// ─── Draht ───────────────────────────────────────────────────────────────────────────────────────

interface WireResponse {
  status: number
  body: Record<string, unknown>
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

/** Der Fehlercode einer Absage — im Haus steht er in `error` (Grossbuchstaben) oder in `code`. */
function codeOf(body: Record<string, unknown>): string | null {
  if (typeof body.code === 'string') return body.code
  if (typeof body.error === 'string' && /^[A-Z][A-Z0-9_]{2,}$/.test(body.error)) return body.error
  return null
}

function messageOf(body: Record<string, unknown>): string | null {
  return typeof body.message === 'string' && body.message !== '' ? body.message : null
}

function quotaOf(body: Record<string, unknown>): QuotaRejection | null {
  const quota = body.quota
  if (!isRecord(quota)) return null
  const { current, limit } = quota
  return typeof current === 'number' && typeof limit === 'number' ? { current, limit } : null
}

function stringList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
}

function schemaIssues(v: unknown): Finding[] {
  if (!Array.isArray(v)) return []
  return v.filter(isRecord).flatMap((issue) => {
    if (typeof issue.message !== 'string') return []
    const path = Array.isArray(issue.path) ? issue.path.map(String).join('.') : ''
    return [{ message: issue.message, ...(path ? { field: path } : {}) }]
  })
}

/** 401/403/429/sonst — was jede Route gleich beantwortet. `null` = die Route-eigene Auswertung entscheidet. */
function commonFailure(res: WireResponse): RequestFailure | null {
  if (res.status === 401 || res.status === 403) return { kind: 'forbidden' }
  if (res.status === 429) {
    const quota = quotaOf(res.body)
    return quota ? { kind: 'quota', quota } : { kind: 'failed', message: messageOf(res.body) }
  }
  return null
}

/** Leere Werte reisen nicht: `dueDate: ''` wuerde der Server als `DATE_EMPTY_IGNORED` melden. */
function withoutEmpty(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(withoutEmpty)
  if (!isRecord(value)) return value
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value)) {
    if (v === null || v === undefined || v === '') continue
    out[k] = withoutEmpty(v)
  }
  return out
}

export function createPdfExtractClient({
  baseUrl,
  getKey,
  refreshKey,
  fetchImpl,
}: PdfExtractClientOptions): PdfExtractClient {
  const doFetch =
    fetchImpl ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init))

  async function post(path: string, key: string, payload: unknown): Promise<WireResponse> {
    const res = await doFetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${key}` },
      body: JSON.stringify(payload),
    })
    const parsed: unknown = await res.json().catch(() => null)
    return { status: res.status, body: isRecord(parsed) ? parsed : {} }
  }

  /** Ein Aufruf mit genau einer Wiederholung nach 401/403 — mit einem frisch ausgegebenen Schluessel (E10). */
  async function send(path: string, payload: unknown): Promise<WireResponse | null> {
    const key = getKey() ?? (await refreshKey())
    if (!key) return null
    const first = await post(path, key, payload)
    if (first.status !== 401 && first.status !== 403) return first
    const fresh = await refreshKey()
    if (!fresh) return first
    return post(path, fresh, payload)
  }

  return {
    async extract(pdf) {
      try {
        const res = await send('/api/v1/invoice/de/pdf/extract', { pdf })
        if (!res) return { ok: false, failure: { kind: 'noKey' } }
        if (res.status === 200) {
          const parsed = extractResponseSchema.safeParse(res.body)
          return parsed.success
            ? { ok: true, result: parsed.data }
            : { ok: false, failure: { kind: 'failed', message: null } }
        }
        const common = commonFailure(res)
        if (common) return { ok: false, failure: common }
        const code = codeOf(res.body)
        if (res.status === 413) return { ok: false, failure: { kind: 'tooLarge' } }
        if (code === 'PDF_INVALID') return { ok: false, failure: { kind: 'invalid' } }
        // Backend vor E19: Die Console kann vor ihm deployt werden, der Code bleibt bis dahin ein Satz auf der Seite.
        if (code === 'PDF_NO_DOCUMENT') return { ok: false, failure: { kind: 'noDocument' } }
        if (code === 'PDF_NO_TEXT') return { ok: false, failure: { kind: 'noText' } }
        if (code === 'SELLER_PROFILE_INCOMPLETE') {
          // Der Plan nennt „mit fehlenden Feldern" (X10), nicht den Feldnamen der Liste: beide Lesarten.
          const missing = stringList(res.body.missing ?? res.body.fields)
          return { ok: false, failure: { kind: 'sellerIncomplete', missing } }
        }
        return { ok: false, failure: { kind: 'failed', message: messageOf(res.body) } }
      } catch (err) {
        return {
          ok: false,
          failure: { kind: 'failed', message: err instanceof Error ? err.message : null },
        }
      }
    },

    async create({ output, pdf, invoice, templateId }) {
      const body: Record<string, unknown> = { invoice: withoutEmpty(invoice), validate: true }
      if (output === 'embed') body.pdf = pdf
      if (output === 'generate' && templateId) body.templateId = templateId
      try {
        const res = await send(OUTPUT_PATH[output], body)
        if (!res) return { ok: false, failure: { kind: 'noKey' } }
        if (res.status === 200) {
          const parsed = createdSchema.safeParse(res.body)
          return parsed.success
            ? { ok: true, result: parsed.data }
            : { ok: false, failure: { kind: 'failed', message: messageOf(res.body) } }
        }
        const common = commonFailure(res)
        if (common) return { ok: false, failure: common }
        // 422: Befunde am erzeugten XML (`complianceErrors`, /embed) oder an der Eingabe (`errors`).
        const findings = z
          .array(findingSchema)
          .safeParse(res.body.complianceErrors ?? res.body.errors)
        if (findings.success && findings.data.length > 0) {
          return { ok: false, failure: { kind: 'compliance', errors: findings.data } }
        }
        // 400 aus dem Eingabeschema: `{ error: [{ path, message }], success: false }` (zod-Issues).
        const issues = schemaIssues(res.body.error)
        if (issues.length > 0) return { ok: false, failure: { kind: 'compliance', errors: issues } }
        return { ok: false, failure: { kind: 'failed', message: messageOf(res.body) } }
      } catch (err) {
        return {
          ok: false,
          failure: { kind: 'failed', message: err instanceof Error ? err.message : null },
        }
      }
    },
  }
}
